/**
 * Reusable Pagination component
 * Usage:
 *   const { page, setPage, paginated, totalPages } = usePagination(items, 10);
 *   ...render paginated items...
 *   <Pagination page={page} totalPages={totalPages} onPageChange={setPage} total={items.length} pageSize={10} />
 */

import { useMemo } from "react";

// ── Hook ──────────────────────────────────────────────────────────────────────
export function usePagination(items, pageSize = 10) {
  const safePageSize = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(items.length / safePageSize));

  // Keep page in bounds if items shrink
  const safePage = (page) => Math.min(Math.max(1, page), totalPages);

  const paginated = (page) =>
    items.slice((page - 1) * safePageSize, page * safePageSize);

  return { totalPages, safePage, paginated };
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function Pagination({ page, totalPages, onPageChange, total, pageSize }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // Build page numbers to show: always show first, last, current ±1, with … gaps
  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, page, page - 1, page + 1].filter(p => p >= 1 && p <= totalPages));
    const sorted = [...set].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("...");
      result.push(sorted[i]);
    }
    return result;
  }, [page, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 20px",
      borderTop: "1px solid var(--border)",
      background: "var(--surface-2)",
      borderRadius: "0 0 12px 12px",
      flexWrap: "wrap",
      gap: 8,
    }}>
      {/* Record count */}
      <span style={{ fontSize: 12, color: "var(--text-3)" }}>
        Showing {from}–{to} of {total}
      </span>

      {/* Page buttons */}
      <nav aria-label="Pagination" style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <PageBtn onClick={() => onPageChange(page - 1)} disabled={page === 1} label="←" ariaLabel="Previous page" />

        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} style={{ padding: "0 4px", color: "var(--text-3)", fontSize: 13 }} aria-hidden="true">…</span>
          ) : (
            <PageBtn
              key={p}
              onClick={() => onPageChange(p)}
              active={p === page}
              label={p}
              ariaLabel={`Page ${p}`}
              ariaCurrent={p === page ? "page" : undefined}
            />
          )
        )}

        <PageBtn onClick={() => onPageChange(page + 1)} disabled={page === totalPages} label="→" ariaLabel="Next page" />
      </nav>
    </div>
  );
}

function PageBtn({ onClick, disabled, active, label, ariaLabel, ariaCurrent }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      style={{
        minWidth: 32,
        height: 32,
        padding: "0 6px",
        borderRadius: 7,
        border: active ? "1.5px solid var(--brand)" : "1px solid var(--border-strong)",
        background: active ? "var(--brand)" : "var(--surface)",
        color: active ? "#fff" : disabled ? "var(--text-3)" : "var(--text-2)",
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {label}
    </button>
  );
}
